
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styles from './Blog.module.css';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Read the selected category from the URL
  const location = useLocation();

  const selectedCategory = new URLSearchParams(location.search)
    .get('category');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const API_URL =
          process.env.REACT_APP_API_URL || 'http://localhost:8000';

        const response = await fetch(`${API_URL}/api/blog/`, {
          headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) throw new Error('API request failed');

        const data = await response.json();

        const formattedPosts = data.map((post) => ({
          ...post,
          category: post.category || 'Insights',
          image:
            post.image ||
            `${process.env.PUBLIC_URL}/img/img${String(
              ((Number(post.id) - 1) % 5) + 1
            ).padStart(2, '0')}.jpeg`,
        }));

        setPosts(formattedPosts);
      } catch (error) {
        console.error('❌ Connection Error:', error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter by category and search term
  const filteredPosts = posts.filter((post) => {
    const matchesSearch = (post.title || '')
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const postCategory = (post.category || 'Insights')
      .trim()
      .toLowerCase();

    const requestedCategory = (selectedCategory || '')
      .trim()
      .toLowerCase();

    const matchesCategory =
      !requestedCategory || postCategory === requestedCategory;

    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingText}>Loading Hub...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Helmet>
        <title>
          {selectedCategory
            ? `${selectedCategory} Articles | Nulek Consulting`
            : 'Insights & Industry Trends | Nulek Consulting Blog'}
        </title>

        <meta
          name="description"
          content={
            selectedCategory
              ? `Explore Nulek Consulting articles on ${selectedCategory}.`
              : 'Stay updated with the latest in project controls, logistics management, and supply chain excellence from Nulek Consulting.'
          }
        />
      </Helmet>

      {/* Hero */}
      <section
        className={`${styles.hero} ${
          isMobile ? styles.heroMobile : ''
        }`}
      >
        <h1
          className={`${styles.heroTitle} ${
            isMobile ? styles.heroTitleMobile : ''
          }`}
        >
          {selectedCategory ? (
            <>
              <span className={styles.heroAccent}>
                {selectedCategory}
              </span>
              {' '}Articles
            </>
          ) : (
            <>
              The <span className={styles.heroAccent}>
                Nulek Consulting
              </span>{' '}
              Blog
            </>
          )}
        </h1>

        <input
          type="text"
          placeholder="Search articles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className={styles.searchInput}
        />

        {selectedCategory && (
          <div>
            <Link
              to="/blog"
              onClick={() => setSearchQuery('')}
              className={styles.readMore}
            >
              ← View All Articles
            </Link>
          </div>
        )}
      </section>

      {/* Posts Grid */}
      <section className={styles.postsSection}>
        <div className={styles.postsContainer}>
          {filteredPosts.length === 0 ? (
            <div className={styles.noResults}>
              <h2>
                {selectedCategory
                  ? `No articles found in "${selectedCategory}".`
                  : 'No articles found.'}
              </h2>

              <p>
                {searchQuery
                  ? 'Try a different search term.'
                  : 'Check your Django Admin to ensure posts exist and their categories are correct.'}
              </p>

              {selectedCategory && (
                <Link to="/blog" className={styles.readMore}>
                  Browse All Articles →
                </Link>
              )}
            </div>
          ) : (
            <>
              <p>
                Showing {filteredPosts.length}{' '}
                {filteredPosts.length === 1 ? 'article' : 'articles'}
                {selectedCategory ? ` in ${selectedCategory}` : ''}
              </p>

              <div className={styles.postsGrid}>
                {filteredPosts.map((post) => (
                  <article
                    key={post.id}
                    className={styles.postCard}
                  >
                    <div className={styles.postImageWrapper}>
                      <img
                        src={post.image}
                        alt={post.title || 'Blog article'}
                        className={styles.postImage}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src =
                            `${process.env.PUBLIC_URL}/img/img01.jpeg`;
                        }}
                      />
                    </div>

                    <div className={styles.postContent}>
                      <div className={styles.postCategory}>
                        {post.category}
                      </div>

                      <h3 className={styles.postTitle}>
                        {post.title}
                      </h3>

                      <p className={styles.postExcerpt}>
                        {post.excerpt ||
                          `${(post.content || '').substring(0, 100)}...`}
                      </p>

                      <Link
                        to={`/blog/${post.id}`}
                        className={styles.readMore}
                      >
                        Read Article →
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;
